import { useEffect } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';

import { styled } from '@mui/material';

import { useUserData } from '@app-velocity/libs/auth';
import { useI18n } from '@app-velocity/i18n';
import {
  isNotFoundError,
  useRedirectOnNotFound,
} from '@app-velocity/common';
import {
  Form,
  TextField,
  BoxContainer,
  Box,
  Skeleton,
  PageWithLogo,
  Typography,
  Button,
} from '@app-velocity/ui';
import {
  usePrototypePasswordToken,
  PasscodeForm,
  UserInfo,
  useUserAccessToFigmaReport,
} from '@app-velocity/modules/password-sharing';

import { useGetSharedShortScenario } from '@app-velocity/modules/scenarios';

import { Authorized } from './Authorized';
import { SimulationReportRoutesPaths } from '../../routes/SimulationReportRoutesPaths';
import { NotAuthorized } from './NotAuthorized';
import { ScenarioInfo } from './components/ScenarioInfo';

const StyledSkeletonBox = styled(Box)({
  width: 554,
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 500px)': {
    width: '100%',
  },
});

export const StartReport = () => {
  const navigate = useNavigate();
  const { shareToken } = useParams<{ shareToken: string }>();

  const {
    data: scenario,
    isLoading: isScenarioLoading,
    isError,
    error,
    isFetched,
  } = useGetSharedShortScenario(shareToken);

  const {
    loading: isAccessLoading,
    accessInfo,
    isError: isAccessError,
    error: accessError,
    isFetched: isAccessFetched,
  } = useUserAccessToFigmaReport(shareToken || '');

  const { userId } = useUserData();
  const { t } = useI18n();

  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<PasscodeForm>({
    mode: 'onSubmit',
    defaultValues: {
      passcode: '',
    },
  });

  const passcode = watch('passcode') || '';

  const {
    mutateAsync: validatePassword,
    isPending,
    error: passwordError,
    isSuccess,
  } = usePrototypePasswordToken(
    shareToken || '',
    passcode,
    'simulationPassword'
  );

  useEffect(() => {
    if (isSuccess && shareToken) {
      setTimeout(() => {
        navigate(SimulationReportRoutesPaths.goToReportOverview(shareToken));
      }, 0);
    }
  }, [isSuccess, navigate, shareToken]);

  useEffect(() => {
    if (!passwordError) return;

    setError('passcode', {
      message: t('prototypes:password_protection.invalid_passcode'),
    });
  }, [passwordError, setError, t]);

  useRedirectOnNotFound(error, isFetched && isError);
  useRedirectOnNotFound(accessError, isAccessFetched && isAccessError);

  const onSubmit = async () => {
    try {
      await validatePassword();
      navigate(
        SimulationReportRoutesPaths.goToReportOverview(shareToken || '')
      );
    } catch {
      return;
    }
  };

  if (isScenarioLoading || isAccessLoading) {
    return (
      <PageWithLogo>
        <StyledSkeletonBox>
          <Skeleton height={100} borderRadius={2} />
          <Skeleton height={300} borderRadius={2} />
        </StyledSkeletonBox>
      </PageWithLogo>
    );
  }

  if (isNotFoundError(error) || isNotFoundError(accessError) || !scenario) {
    return null;
  }

  if (!accessInfo) return null;

  const { authRequired, passwordRequired } = accessInfo;

  return (
    <PageWithLogo>
      <ScenarioInfo scenario={scenario} />

      {authRequired ? (
        <BoxContainer>
          {userId ? (
            <>
              <Typography>{t('websites:access.no_access')}</Typography>
              <UserInfo />
              <Form.ButtonsContainer>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  component={NavLink}
                  to="/"
                >
                  {t('shared:go_to_homepage')}
                </Button>
              </Form.ButtonsContainer>
            </>
          ) : (
            <NotAuthorized isPending={false} />
          )}
        </BoxContainer>
      ) : null}

      {passwordRequired ? (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <BoxContainer>
            <Form.FieldContainer>
              <Form.Label>
                {t('prototypes:password_protection.title')}
              </Form.Label>
              <TextField
                placeholder={t('prototypes:password_protection.placeholder')}
                fullWidth
                control={control}
                name="passcode"
                helperText={errors.passcode?.message}
                error={!!errors.passcode}
                inputProps={{
                  autoComplete: 'current-password',
                }}
              />
            </Form.FieldContainer>
          </BoxContainer>
          <BoxContainer>
            <UserInfo />
            {userId ? (
              <Authorized isPending={isPending} />
            ) : (
              <Form.ButtonsContainer>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isPending}
                >
                  {t('shared:continue')}
                </Button>
                <NotAuthorized
                  isPending={isPending}
                  color="secondary"
                  fullWidth
                />
              </Form.ButtonsContainer>
            )}
          </BoxContainer>
        </Form>
      ) : null}
    </PageWithLogo>
  );
};
